import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { getUnixTime, parse } from "date-fns";
import { startCase, toLower } from "lodash";
import { useMemo } from "react";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";

import { WeatherIcon } from "@whoosh/design-system/src/components/icons_v2/weather";
import { HStack, Text } from "@whoosh/design-system/src/components/primitive";

import { CurrentClubWeatherInnerQuery } from "./__generated__/CurrentClubWeatherInnerQuery.graphql";

type CurrentClubWeatherInnerProps = {
  queryRef: PreloadedQuery<CurrentClubWeatherInnerQuery>;
  showMinimal?: boolean;
};

export const CurrentClubWeatherInner = ({
  queryRef,
  showMinimal,
}: CurrentClubWeatherInnerProps) => {
  const data = usePreloadedQuery(CurrentClubWeatherInnerQueryString, queryRef);
  const weatherConditions = useMemo(() => {
    return (
      data.session?.currentClientProfile?.member?.club.facilities?.edges?.[0]
        ?.node?.agendas?.edges?.[0]?.node?.weatherConditions?.edges ?? []
    );
  }, [data.session?.currentClientProfile?.member?.club.facilities?.edges]);

  const currentCondition = useMemo(() => {
    const now = getUnixTime(new Date());

    const sortedConditions = [...weatherConditions].sort((a, b) => {
      const startTimeA =
        a?.node?.startTime && parse(a?.node?.startTime, "HH:mm:ss", new Date());
      const startTimeB =
        b?.node?.startTime && parse(b?.node?.startTime, "HH:mm:ss", new Date());

      const diffA = startTimeA ? Math.abs(now - getUnixTime(startTimeA)) : 0;
      const diffB = startTimeB ? Math.abs(now - getUnixTime(startTimeB)) : 0;

      return diffA - diffB;
    });

    return sortedConditions[0]?.node;
  }, [weatherConditions]);

  if (!currentCondition) return null;

  const weatherCategory = startCase(toLower(currentCondition?.weatherCategory));
  const tempUnit = currentCondition.measurementSystem === "METRIC" ? "C" : "F";
  const windUnit =
    currentCondition.measurementSystem === "METRIC" ? "m/s" : "mph";
  const temperature = Math.ceil(currentCondition?.temperature ?? 0);
  const humidityInPercent = Math.round(
    currentCondition?.humidityInPercent ?? 0 * 100
  );
  const windSpeedInMph = Math.round(currentCondition?.windSpeedInMph ?? 0);

  const minimumWeatherString = `${weatherCategory}, ${temperature}° ${tempUnit}, ${t`Wind`}: ${windSpeedInMph} ${windUnit}`;
  const allWeatherSrting = `${weatherCategory}, ${temperature}° ${tempUnit}, ${t`Humidity`}: ${humidityInPercent}%, ${t`Wind`}: ${windSpeedInMph} ${windUnit}`;

  return (
    <HStack gap={2} alignItems="center">
      {currentCondition?.weatherCategory && !showMinimal ? (
        <WeatherIcon weatherCategory={currentCondition?.weatherCategory} />
      ) : null}
      <Text
        variant="mainTiny"
        color={showMinimal ? "text-secondary" : "text-primary"}
      >
        {showMinimal ? minimumWeatherString : allWeatherSrting}
      </Text>
    </HStack>
  );
};

export const CurrentClubWeatherInnerQueryString = graphql`
  query CurrentClubWeatherInnerQuery($date: Date!) {
    session {
      currentClientProfile {
        member {
          club {
            id
            facilities {
              edges {
                node {
                  id
                  agendas(dateGte: $date, dateLte: $date) {
                    edges {
                      node {
                        weatherConditions(first: 1000) {
                          edges {
                            node {
                              startTime
                              id
                              weatherCategory
                              probabilityOfPrecipitationInPercent
                              temperature
                              measurementSystem
                              humidityInPercent
                              windDegrees
                              windSpeedInMph
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
